"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useQuery, useMutation } from "convex/react";
import { useAuth } from "@clerk/clerk-react";
import { api } from "../../../convex/_generated/api";
import { useDebounce, useIsDebouncing } from "@/lib/useDebounce";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { usePageTitle } from "@/lib/usePageTitle";
import { cn } from "@/lib/utils";
import {
  SearchIcon,
  PluginIcon,
  DownloadIcon,
  StarIcon,
  StarFilledIcon,
  EyeIcon,
  TagIcon,
  UploadIcon,
  ArrowRightIcon,
} from "@/components/icons";
import type { Id } from "../../../convex/_generated/dataModel";

type SortOption = "recent" | "popular" | "downloads" | "stars";

type MarketplaceFinderPlugin = {
  sourcePluginId: string;
  slug: string;
  name: string;
  shortDescription: string;
  version: string;
  authorName: string;
  tags: string[];
  totalDownloads: number;
  updatedAt: number;
  createdAt: number;
  isPaid: boolean;
  priceInCents: number;
  pluginUrl?: string;
  libraryUrl?: string;
};

type FinderFeedResponse = {
  plugins?: MarketplaceFinderPlugin[];
};

type LocalUnifiedPlugin = {
  source: "local";
  key: string;
  _id: Id<"plugins">;
  name: string;
  slug: string;
  shortDescription: string;
  version: string;
  viewCount: number;
  downloadCount: number;
  starCount: number;
  tags: string[];
  uploaderName: string;
  createdAt: number;
  updatedAt: number;
};

type MarketplaceUnifiedPlugin = {
  source: "blockymarketplace";
  key: string;
  name: string;
  slug: string;
  shortDescription: string;
  version: string;
  downloadCount: number;
  starCount: number;
  viewCount: number;
  tags: string[];
  uploaderName: string;
  createdAt: number;
  updatedAt: number;
  isPaid: boolean;
  priceInCents: number;
  externalUrl: string;
};

type UnifiedPlugin = LocalUnifiedPlugin | MarketplaceUnifiedPlugin;

function isOfficialCompanionPlugin(plugin: { name: string; slug: string; tags: string[] }) {
  const n = plugin.name.toLowerCase();
  const s = plugin.slug.toLowerCase();
  const tags = plugin.tags.map((t) => t.toLowerCase());

  return (
    s === "official-companion" ||
    s === "blockynetwork" ||
    s.includes("official-companion") ||
    n.includes("official hytale companion") ||
    n.includes("blockynetwork plugin") ||
    tags.includes("official")
  );
}

function normalizeTag(value: string) {
  return value.toLowerCase().trim();
}

function formatPrice(cents: number) {
  if (cents <= 0) return "Free";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

export default function PluginsPage() {
  usePageTitle("Hytale Plugins");
  const { isSignedIn } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const isSearching = useIsDebouncing(searchQuery, debouncedSearch);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("recent");

  const rawBmApiUrl = (import.meta.env.VITE_BM_API_URL as string | undefined) ?? "";
  const bmApiUrl = rawBmApiUrl.trim().replace(/\/+$/, "");
  const hasBmIntegration = bmApiUrl.length > 0;

  const [marketplacePlugins, setMarketplacePlugins] = useState<MarketplaceFinderPlugin[]>([]);
  const [marketplaceLoading, setMarketplaceLoading] = useState(false);
  const [marketplaceError, setMarketplaceError] = useState<string | null>(null);

  const localPlugins = useQuery(api.plugins.list, {
    sort: "recent",
    limit: 200,
  });

  useEffect(() => {
    if (!hasBmIntegration) {
      setMarketplacePlugins([]);
      setMarketplaceError(null);
      setMarketplaceLoading(false);
      return;
    }

    let cancelled = false;
    setMarketplaceLoading(true);
    setMarketplaceError(null);

    fetch(`${bmApiUrl}/api/plugins/finder-feed`)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to load BlockyMarketplace plugin feed");
        }
        return res.json() as Promise<FinderFeedResponse>;
      })
      .then((data) => {
        if (cancelled) return;
        setMarketplacePlugins(Array.isArray(data.plugins) ? data.plugins : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setMarketplaceError(
          err instanceof Error ? err.message : "Failed to load BlockyMarketplace plugin feed"
        );
        setMarketplacePlugins([]);
      })
      .finally(() => {
        if (!cancelled) {
          setMarketplaceLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [bmApiUrl, hasBmIntegration]);

  const localCommunityPlugins = useMemo(() => {
    return localPlugins?.filter((plugin) => !isOfficialCompanionPlugin(plugin));
  }, [localPlugins]);

  const unifiedPlugins = useMemo<UnifiedPlugin[] | undefined>(() => {
    if (!localCommunityPlugins) {
      return undefined;
    }

    const localMapped: LocalUnifiedPlugin[] = localCommunityPlugins.map((plugin) => ({
      source: "local",
      key: `local:${plugin._id}`,
      _id: plugin._id,
      name: plugin.name,
      slug: plugin.slug,
      shortDescription: plugin.shortDescription,
      version: plugin.version,
      viewCount: plugin.viewCount,
      downloadCount: plugin.downloadCount,
      starCount: plugin.starCount,
      tags: plugin.tags,
      uploaderName: plugin.uploaderName,
      createdAt: plugin.createdAt,
      updatedAt: plugin.updatedAt,
    }));

    const localSlugs = new Set(localMapped.map((plugin) => plugin.slug.toLowerCase()));

    const marketplaceMapped: MarketplaceUnifiedPlugin[] = marketplacePlugins
      .filter((plugin) => !localSlugs.has(plugin.slug.toLowerCase()))
      .map((plugin) => ({
        source: "blockymarketplace",
        key: `blockymarketplace:${plugin.sourcePluginId}`,
        name: plugin.name,
        slug: plugin.slug,
        shortDescription: plugin.shortDescription,
        version: plugin.version,
        viewCount: 0,
        downloadCount: plugin.totalDownloads,
        starCount: 0,
        tags: plugin.tags,
        uploaderName: plugin.authorName,
        createdAt: plugin.createdAt,
        updatedAt: plugin.updatedAt,
        isPaid: plugin.isPaid,
        priceInCents: plugin.priceInCents,
        externalUrl: plugin.pluginUrl || `${bmApiUrl}/plugins/${plugin.slug}`,
      }));

    return [...localMapped, ...marketplaceMapped];
  }, [localCommunityPlugins, marketplacePlugins, bmApiUrl]);

  const filteredPlugins = useMemo<UnifiedPlugin[] | undefined>(() => {
    if (!unifiedPlugins) return undefined;

    let plugins = [...unifiedPlugins];

    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase();
      plugins = plugins.filter((plugin) => {
        const matchesName = plugin.name.toLowerCase().includes(query);
        const matchesDescription = plugin.shortDescription.toLowerCase().includes(query);
        const matchesAuthor = plugin.uploaderName.toLowerCase().includes(query);
        const matchesTags = plugin.tags.some((tag) => tag.toLowerCase().includes(query));
        return matchesName || matchesDescription || matchesAuthor || matchesTags;
      });
    }

    if (selectedTag) {
      const selected = normalizeTag(selectedTag);
      plugins = plugins.filter((plugin) =>
        plugin.tags.some((tag) => normalizeTag(tag) === selected)
      );
    }

    switch (sortBy) {
      case "recent":
        plugins.sort((a, b) => b.updatedAt - a.updatedAt);
        break;
      case "popular":
        plugins.sort(
          (a, b) => b.downloadCount + b.starCount - (a.downloadCount + a.starCount)
        );
        break;
      case "downloads":
        plugins.sort((a, b) => b.downloadCount - a.downloadCount);
        break;
      case "stars":
        plugins.sort((a, b) => b.starCount - a.starCount);
        break;
    }

    return plugins.slice(0, 50);
  }, [unifiedPlugins, debouncedSearch, selectedTag, sortBy]);

  const popularTags = useMemo(() => {
    if (!unifiedPlugins) return [];

    const tagCounts = new Map<string, number>();

    for (const plugin of unifiedPlugins) {
      const uniqueTags = new Set(plugin.tags.map(normalizeTag).filter(Boolean));
      for (const tag of uniqueTags) {
        tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
      }
    }

    return [...tagCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15)
      .map(([tag, count]) => ({ tag, count }));
  }, [unifiedPlugins]);

  const marketplaceLibraryUrl = useMemo(() => {
    if (!hasBmIntegration) return null;
    const firstFeedLibraryUrl = marketplacePlugins.find((p) => p.libraryUrl)?.libraryUrl;
    return firstFeedLibraryUrl || `${bmApiUrl}/plugins/library`;
  }, [hasBmIntegration, marketplacePlugins, bmApiUrl]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        {/* Header Section */}
        <section className="border-b border-border bg-foreground/5 py-12">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-3 text-primary">
                  <PluginIcon className="h-8 w-8" />
                </div>
                <div>
                  <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
                    Hytale Plugin Finder
                    <InfoHint text="Discover and download community-built plugins for your Hytale server, including synced BlockyMarketplace plugins." />
                  </h1>
                  <p className="text-muted">
                    Discover community plugins for your Hytale server
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {isSignedIn && marketplaceLibraryUrl && (
                  <a
                    href={marketplaceLibraryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={buttonVariants({ variant: "outline" })}
                  >
                    <DownloadIcon className="mr-2 h-4 w-4" />
                    My Purchased Plugins
                  </a>
                )}
                {isSignedIn && (
                  <Link href="/plugins/upload">
                    <Button>
                      <UploadIcon className="mr-2 h-4 w-4" />
                      Upload Plugin
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            {/* Companion plugin lives in Downloads + Documentation */}
            <div className="mt-6">
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Need the BlockyNetwork companion plugin?
                    </p>
                    <p className="text-sm text-muted">
                      Use Downloads for the `.jar` and Documentation for setup steps. Marketplace paid plugins open on BlockyMarketplace.
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Link href="/downloads">
                      <Button variant="outline" size="sm">
                        Downloads
                        <DownloadIcon className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Link href="/documentation">
                      <Button variant="outline" size="sm">
                        Documentation
                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
              {marketplaceError && (
                <p className="mt-3 text-sm text-muted">
                  BlockyMarketplace plugins are temporarily unavailable. Local plugin listings are still visible.
                </p>
              )}
            </div>

            {/* Search */}
            <div className="mt-8 flex gap-3">
              <div className="relative flex-1">
                <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                <Input
                  type="text"
                  placeholder="Search plugins by name, author, or tag..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-8"
                  aria-label="Search plugins"
                />
                {isSearching && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    <svg className="h-4 w-4 animate-spin text-muted" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                    </svg>
                  </span>
                )}
              </div>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="rounded-md px-3 text-sm text-muted hover:text-foreground transition-colors border border-border"
                  aria-label="Clear search"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Sort chips */}
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <span className="text-xs text-muted uppercase tracking-wide">Sort:</span>
              {(["recent", "popular", "downloads", "stars"] as SortOption[]).map((opt) => {
                const labels: Record<SortOption, string> = {
                  recent: "Recent",
                  popular: "Popular",
                  downloads: "Downloads",
                  stars: "Stars",
                };
                return (
                  <button
                    key={opt}
                    onClick={() => setSortBy(opt)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      sortBy === opt
                        ? "bg-primary text-primary-foreground"
                        : "bg-foreground/5 text-muted hover:bg-foreground/10 border border-border"
                    }`}
                    aria-pressed={sortBy === opt}
                  >
                    {labels[opt]}
                  </button>
                );
              })}
              {(debouncedSearch || selectedTag) && (
                <button
                  onClick={() => { setSearchQuery(""); setSelectedTag(null); }}
                  className="ml-auto rounded-full px-3 py-1 text-xs text-destructive hover:bg-destructive/10 border border-destructive/30 transition-colors"
                  aria-label="Clear all filters"
                >
                  ✕ Clear filters
                </button>
              )}
            </div>

            {/* Tag filter chips */}
            {popularTags.length > 0 && (
              <div className="mt-4 overflow-x-auto pb-1">
                <div className="flex flex-wrap gap-2 min-w-max sm:min-w-0">
                <button
                  onClick={() => setSelectedTag(null)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                    selectedTag === null
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "bg-foreground/5 text-muted hover:bg-foreground/10 border border-border/60"
                  }`}
                  aria-pressed={selectedTag === null}
                >
                  All
                </button>
                {popularTags.map(({ tag }) => {
                  const isActive = selectedTag === tag;
                  return (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(isActive ? null : tag)}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "bg-foreground/5 text-muted hover:bg-foreground/10 border border-border/60"
                    }`}
                    aria-pressed={isActive}
                  >
                    <TagIcon className="h-3 w-3 shrink-0" />
                    {tag}
                    {isActive && <span className="ml-0.5 opacity-70">✕</span>}
                  </button>
                  );
                })}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Plugin Grid */}
        <section className="py-8">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            {filteredPlugins === undefined ? (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-56" />
                ))}
              </div>
            ) : filteredPlugins.length === 0 ? (
              <div className="py-20 text-center">
                <PluginIcon className="mx-auto h-12 w-12 text-muted" />
                <h2 className="mt-4 text-xl font-semibold text-foreground">
                  No plugins found
                </h2>
                <p className="mt-2 text-muted">
                  {debouncedSearch || selectedTag
                    ? "Try a different search or filter"
                    : "Be the first to upload a plugin!"}
                </p>
                {isSignedIn && (
                  <Link href="/plugins/upload" className="mt-6 inline-block">
                    <Button>Upload Your First Plugin</Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filteredPlugins.map((plugin) =>
                  plugin.source === "local" ? (
                    <LocalPluginCard key={plugin.key} plugin={plugin} />
                  ) : (
                    <MarketplacePluginCard key={plugin.key} plugin={plugin} />
                  )
                )}
              </div>
            )}
            {hasBmIntegration && marketplaceLoading && filteredPlugins !== undefined && (
              <p className="mt-4 text-xs text-muted">Syncing BlockyMarketplace plugins…</p>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function LocalPluginCard({ plugin }: { plugin: LocalUnifiedPlugin }) {
  const { isSignedIn } = useAuth();
  const hasStarred = useQuery(
    api.plugins.hasStarred,
    isSignedIn ? { pluginId: plugin._id } : "skip"
  );
  const toggleStar = useMutation(api.plugins.toggleStar);

  const handleStarClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSignedIn) return;
    await toggleStar({ pluginId: plugin._id });
  };

  return (
    <Card className="group relative overflow-hidden transition-all hover:border-foreground/15 hover:bg-foreground/[0.02] hover:shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-start justify-between">
          <div>
            <span className="text-lg">{plugin.name}</span>
            <span className="ml-2 text-sm font-normal text-muted">v{plugin.version}</span>
          </div>
          {isSignedIn && (
            <button
              onClick={handleStarClick}
              className="text-muted transition-colors hover:text-yellow-500"
            >
              {hasStarred ? (
                <StarFilledIcon className="h-5 w-5 text-yellow-500" />
              ) : (
                <StarIcon className="h-5 w-5" />
              )}
            </button>
          )}
        </CardTitle>
        <p className="line-clamp-2 text-sm text-muted">{plugin.shortDescription}</p>
        <p className="text-xs text-muted/70">by {plugin.uploaderName}</p>
      </CardHeader>

      <CardContent className="space-y-4">
        {plugin.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {plugin.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
            {plugin.tags.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{plugin.tags.length - 3}
              </Badge>
            )}
          </div>
        )}

        <div className="flex items-center gap-4 text-sm text-muted">
          <div className="flex items-center gap-1" title="Views">
            <EyeIcon className="h-4 w-4" />
            <span>{plugin.viewCount} views</span>
          </div>
          <div className="flex items-center gap-1" title="Downloads">
            <DownloadIcon className="h-4 w-4" />
            <span>{plugin.downloadCount} downloads</span>
          </div>
          <div className="flex items-center gap-1" title="Stars">
            <StarIcon className="h-4 w-4" />
            <span>{plugin.starCount} stars</span>
          </div>
        </div>

        <Link href={`/plugins/${plugin.slug}`} className="block">
          <Button className="w-full" variant="outline">
            View Plugin
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function MarketplacePluginCard({ plugin }: { plugin: MarketplaceUnifiedPlugin }) {
  return (
    <Card className="group relative overflow-hidden border-primary/20 transition-all hover:border-primary/35 hover:bg-primary/[0.03] hover:shadow-sm">
      <CardHeader className="pb-2">
        <div className="mb-2 flex items-center justify-between gap-2">
          <Badge variant="secondary">BlockyMarketplace</Badge>
          <Badge variant={plugin.isPaid ? "default" : "secondary"}>
            {formatPrice(plugin.priceInCents)}
          </Badge>
        </div>
        <CardTitle>
          <span className="text-lg">{plugin.name}</span>
          <span className="ml-2 text-sm font-normal text-muted">v{plugin.version}</span>
        </CardTitle>
        <p className="line-clamp-2 text-sm text-muted">{plugin.shortDescription}</p>
        <p className="text-xs text-muted/70">by {plugin.uploaderName}</p>
      </CardHeader>

      <CardContent className="space-y-4">
        {plugin.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {plugin.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
            {plugin.tags.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{plugin.tags.length - 3}
              </Badge>
            )}
          </div>
        )}

        <div className="flex items-center gap-4 text-sm text-muted">
          <div className="flex items-center gap-1" title="Downloads">
            <DownloadIcon className="h-4 w-4" />
            <span>{plugin.downloadCount} downloads</span>
          </div>
          <div className="flex items-center gap-1" title="Source">
            <TagIcon className="h-4 w-4" />
            <span>Marketplace</span>
          </div>
        </div>

        <a
          href={plugin.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(buttonVariants({ variant: "outline" }), "w-full")}
        >
          {plugin.isPaid ? "Buy on BlockyMarketplace" : "View on BlockyMarketplace"}
          <ArrowRightIcon className="ml-2 h-4 w-4" />
        </a>
      </CardContent>
    </Card>
  );
}
